import { styled } from "goober";
import { useContext, useEffect, useRef, useState } from "react";
import { compact } from "../../lib/compact";
import { ROUTE_TYPE_ICONS, routeTypeLabel } from "../../lib/routeTypeDisplay";
import { zIndexes } from "../../lib/zIndexes";
import { AnchoredPopover } from "../AnchoredPopover";
import { ConnectorAvatars } from "../ConnectorAvatars";
import { Icon, type IconName } from "../Icon";
import { isLinkTooltipOpen, onLinkTooltipsClosed } from "../LinkTooltip";
import { Text } from "../Text";
import {
  getLastTooltipCloseTime,
  setLastTooltipCloseTime,
  TOOLTIP_DELAY,
  WARM_PERIOD,
} from "../Tooltip";
import type { StepData } from "../Workflow";
import { StepContextMenuOpenContext } from "./StepContextMenu";

const CARD_WIDTH = 240;
const EDGE_GUARD = 140;

const Popover = styled(AnchoredPopover)`
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  --text-secondary: var(--tooltip-fg-subtle);
  /* The surface is pinned dark in both modes, so tell theme-aware connector
     logos (light-dark() SVGs) to paint their dark-surface variant here. */
  color-scheme: dark;
  box-shadow: var(--elevation-low);
  padding: 8px 10px;
  z-index: ${zIndexes.tooltip};
  width: ${CARD_WIDTH}px;
  text-align: left;
  font-size: 10px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 6px;

  .counter,
  .metadata-badge {
    background: var(--tooltip-highlight);
    color: var(--tooltip-fg);
    font-size: 9px;
    font-weight: 500;
    line-height: 12px;
    letter-spacing: -1%;
    display: inline-flex;
    min-width: 16px;
    width: fit-content;
    border-radius: 10px;
    padding: 2px 6px;
    justify-content: center;
    flex-shrink: 0;
  }

  .metadata-badge {
    align-items: center;
    gap: 4px;
    padding: 2px 8px 2px 6px;
    white-space: nowrap;
  }
`;

const MetadataLogos = styled("div")`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  max-width: 100%;
  margin-left: auto;

  & > div {
    flex-wrap: wrap;
  }
`;

type ConnectedAvatar = React.ComponentProps<
  typeof ConnectorAvatars
>["connected"];
type DisconnectedAvatar = NonNullable<
  React.ComponentProps<typeof ConnectorAvatars>["disconnected"]
>;

type Props = {
  step: StepData;
  hovered: boolean;
  anchorName: string;
  anchorRef: React.RefObject<HTMLElement | null>;
  incomingFromStepIds: string[];
  isChanged: boolean;
  connected: ConnectedAvatar;
  disconnected: DisconnectedAvatar;
};

// The compact card tiers hide the full card's counter, badges, logos, and
// description, so hovering a card surfaces them in a flyout above the card
// (flipping when the viewport is in the way). Its metadata and full connector
// strip share one flex wrap, with connectors right-aligned while one row fits.
export function StepHoverCard({
  step,
  hovered,
  anchorName,
  anchorRef,
  incomingFromStepIds,
  isChanged,
  connected,
  disconnected,
}: Props) {
  const isContextMenuOpen = useContext(StepContextMenuOpenContext);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hovered && !isContextMenuOpen) {
      const show = () => {
        setPosition(computePosition(anchorRef.current));
        setOpen(true);
      };
      const schedule = () => {
        if (Date.now() - getLastTooltipCloseTime() < WARM_PERIOD) {
          show();
          return;
        }
        openTimerRef.current = setTimeout(show, TOOLTIP_DELAY);
      };
      const unsubscribe = isLinkTooltipOpen()
        ? onLinkTooltipsClosed(schedule)
        : undefined;
      if (!unsubscribe) schedule();
      return () => {
        unsubscribe?.();
        if (openTimerRef.current) {
          clearTimeout(openTimerRef.current);
          openTimerRef.current = null;
        }
      };
    }
    // oxlint-disable-next-line react/react-compiler -- EffectSetState: closes the card and records the close time in the shared warm-period store on unhover
    setOpen((wasOpen) => {
      if (wasOpen) setLastTooltipCloseTime(Date.now());
      return false;
    });
  }, [hovered, isContextMenuOpen, anchorRef]);

  const badges = compact<{ icon: IconName; label: string }>([
    incomingFromStepIds.length > 1
      ? { icon: "input", label: "Multiple inputs" }
      : null,
    step.schedule?.cronExpression
      ? { icon: "clock", label: "Scheduled" }
      : null,
    step.hasConflicts ? { icon: "merge", label: "Merge conflict" } : null,
    step.hasOutput || step.routeType
      ? step.routeType
        ? {
            icon: ROUTE_TYPE_ICONS[step.routeType],
            label: routeTypeLabel(step.routeType),
          }
        : { icon: "earth", label: "Web request" }
      : null,
    step.hasStorage ? { icon: "box", label: "Storage" } : null,
    isChanged ? { icon: "traffic-cone", label: "Changed" } : null,
  ]);

  if (!open) return null;

  const hasLogos = connected.length > 0 || disconnected.length > 0;

  return (
    <Popover isOpen target={anchorName} style={position} trigger={() => null}>
      <Text variant="text-small-md">{step.name}</Text>
      {step.readmeDescription && (
        <Text variant="text-small" color="var(--tooltip-fg-subtle)">
          {step.readmeDescription}
        </Text>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        <span className="counter">{step.executionCount.toLocaleString()}</span>
        {badges.map(({ icon, label }) => (
          <span className="metadata-badge" key={label}>
            <Icon name={icon} size={10} color="var(--tooltip-fg-muted)" />
            {label}
          </span>
        ))}
        {hasLogos && (
          <MetadataLogos>
            <ConnectorAvatars
              flat
              align="end"
              connected={connected}
              disconnected={disconnected}
            />
          </MetadataLogos>
        )}
      </div>
    </Popover>
  );
}

function computePosition(anchor: HTMLElement | null): React.CSSProperties {
  const styles: Record<string, string> = {};
  if (!anchor) return styles;
  const rect = anchor.getBoundingClientRect();

  if (rect.top < EDGE_GUARD) {
    styles.top = "anchor(bottom)";
    styles.marginTop = "6px";
  } else {
    styles.bottom = "anchor(top)";
    styles.marginBottom = "6px";
  }

  if (window.innerWidth - rect.left < CARD_WIDTH + 20) {
    styles.right = "anchor(right)";
  } else {
    styles.left = "anchor(left)";
  }

  return styles as React.CSSProperties;
}
